import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "react-oidc-context";

const ROOM_TAG_OPTIONS = [
  { value: "Aucun", label: "Aucun" },
  { value: "Témoin", label: "Témoin" },
  { value: "Essentiel", label: "Essentiel" },
  { value: "Expérience", label: "Expérience" },
];

export function EditRoomModal({ isOpen, onClose, room, modules = [], hotelId, masterId, onRoomUpdated }) {
  const [formData, setFormData] = useState({
    room_nb: "",
    qrId: "",
    rTag: "Aucun",
  });
  const [isSaving, setIsSaving] = useState(false);

  // IDs pour les différentes bases de données
  const managerRoomId = room.room_id || room.id || room.manager_room_id; // ID dans la base manager
  const greenMilesRoomId = room.rId || room.id; // ID dans la base GreenMiles
  const greenMilesHotelId = masterId; // ID de l'hôtel dans la base GreenMiles

  const auth = useAuth();
  const token = auth.user?.access_token;
  const API_URL = process.env.REACT_APP_API_URL || "/api";

  useEffect(() => {
    if (isOpen && room) {
      const roomNb = room.room_nb ?? room.rRoomNumber ?? "";
      setFormData({
        room_nb: String(roomNb),
        qrId: room.qrId ?? "",
        rTag: room.rTag ?? "Aucun",
      });
    }
  }, [isOpen, room]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    if (!formData.room_nb.trim()) {
      alert("Veuillez entrer un numéro de chambre.");
      setIsSaving(false);
      return;
    }

    try {
      const promises = [];
      const currentRoomNb = room.room_nb ?? room.rRoomNumber ?? "";

      // Mise à jour du numéro de chambre si changé (nécessite manager_id et manager_room_id)
      if (String(formData.room_nb).trim() !== String(currentRoomNb).trim() && hotelId && managerRoomId) {
        promises.push(
          axios.patch(
            `${API_URL}/hotels/${hotelId}/rooms/${managerRoomId}`,
            { room_nb: formData.room_nb },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
        );
      }

      // Mise à jour du QR Code GreenMiles si changé (nécessite masterId et rId)
      const currentQrId = room.qrId || "";
      const newQrId = formData.qrId.trim();
      if (newQrId !== currentQrId && greenMilesHotelId && greenMilesRoomId) {
        // Si le QR code est vide, on ne fait rien (pas de suppression automatique)
        // L'utilisateur doit explicitement supprimer via l'interface si nécessaire
        if (newQrId !== "") {
          promises.push(
            axios.patch(
              `${API_URL}/greenmiles/hotels/${greenMilesHotelId}/rooms/${greenMilesRoomId}/qr`,
              { qrId: newQrId },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            )
          );
        }
      }

      // Mise à jour du tag de chambre (rTag) si changé
      const currentRTag = room.rTag ?? "Aucun";
      if (formData.rTag !== currentRTag && greenMilesHotelId && greenMilesRoomId) {
        promises.push(
          axios.patch(
            `${API_URL}/greenmiles/hotels/${greenMilesHotelId}/rooms/${greenMilesRoomId}/tag`,
            { rTag: formData.rTag },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
        );
      }

      // Vérifier qu'il y a au moins une modification
      if (promises.length === 0) {
        alert("Aucune modification détectée.");
        setIsSaving(false);
        return;
      }

      await Promise.all(promises);

      if (onRoomUpdated) {
        onRoomUpdated();
      }
      onClose();
    } catch (err) {
      console.error("Error updating room:", err);
      alert("Erreur lors de la mise à jour de la chambre");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
      <div className="relative bg-white p-8 rounded-lg shadow-lg max-w-lg w-full">
        <h2 className="text-xl font-bold text-center mb-6 text-gray-800">Modification de la chambre {room.room_nb ?? room.rRoomNumber ?? "—"}</h2>
        <button type="button" className="absolute top-4 right-8 text-gray-600 hover:text-gray-900 text-2xl" onClick={onClose} aria-label="Close" disabled={isSaving}>
          &times;
        </button>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Numéro de chambre */}
          <div className="flex flex-col">
            <label className="mb-2 font-medium text-gray-700">Numéro de chambre</label>
            <input type="text" name="room_nb" value={formData.room_nb} onChange={handleChange} className="border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#365644] text-black" placeholder="Numéro de la chambre" required disabled={isSaving} />
          </div>

          {/* Tag de chambre (rTag) */}
          <div className="flex flex-col">
            <label className="mb-2 font-medium text-gray-700">Type chambre</label>
            <select
              name="rTag"
              value={formData.rTag}
              onChange={handleChange}
              className="border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#365644] text-black bg-white"
              disabled={isSaving}
            >
              {ROOM_TAG_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-1">Les chambres témoin n'affichent pas l'action douche GreenMiles.</p>
          </div>

          {/* QR Code GreenMiles */}
          <div className="flex flex-col">
            <label className="mb-2 font-medium text-gray-700">QR Code GreenMiles</label>
            <input type="text" name="qrId" value={formData.qrId} onChange={handleChange} className="border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#365644] text-black" placeholder="ID du QR code GreenMiles" disabled={isSaving} />
            <p className="text-sm text-gray-500 mt-1">Entrez l'ID du QR code GreenMiles pour cette chambre</p>
          </div>

          {/* Boutons */}
          <div className="flex space-x-4">
            <button type="button" onClick={onClose} className="flex-1 bg-gray-300 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-400 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isSaving}>
              Annuler
            </button>
            <button type="submit" className="flex-1 bg-[#365644] text-white px-4 py-3 rounded-lg hover:bg-[#2d4737] transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isSaving}>
              {isSaving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
